import { type AdminQuoteDetail, formatSizeBreakdown, sizeTotalsOf } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount, formatDateTime } from '@/shared/lib';
import { ContentCard, detailListStyles as styles } from '@/shared/ui';
import { QuoteDestinationsTable } from './quote-destinations-table';

export interface QuoteRequestInfoProps {
  readonly detail: AdminQuoteDetail;
}

/**
 * 요청 내용(견적 설계 §6-2) — 고객이 요청 때 입력한 값 전부와 고정된 상품·색상·인쇄 방식 사본. 배송지는 배송지별 표(SUPER-86 §6-2)로,
 * 비회원 출처 배송지는 받는 분·연락처·상세주소 없이 보인다(주문할 때 받는다, D8).
 */
export const QuoteRequestInfo = ({ detail }: QuoteRequestInfoProps): ReactElement => {
  const destinationCount = detail.destinations.length;
  const sizeBreakdown = formatSizeBreakdown(sizeTotalsOf(detail.destinations));

  return (
    <ContentCard title="요청 내용">
      <dl className={styles.list}>
        <dt className={styles.term}>견적 방식</dt>
        <dd>{detail.design.editAreaId === null ? '인쇄 없음(일반 견적)' : '디자인 견적'}</dd>
        <dt className={styles.term}>상품</dt>
        <dd>{detail.productName}</dd>
        <dt className={styles.term}>색상</dt>
        <dd>{detail.design.colorName}</dd>
        <dt className={styles.term}>인쇄 방식</dt>
        <dd>{detail.design.printMethodName ?? '없음'}</dd>
        <dt className={styles.term}>수량</dt>
        <dd>
          {formatCount(detail.quantity)}개
          {destinationCount > 0 ? ` · 배송지 ${destinationCount}곳` : ''}
          {sizeBreakdown ? ` · ${sizeBreakdown}` : ''}
        </dd>
        <dt className={styles.term}>요청일</dt>
        <dd>{formatDateTime(detail.createdAt)}</dd>
        <dt className={styles.term}>희망 납기일</dt>
        <dd>{detail.desiredDate ?? <span className={styles.empty}>-</span>}</dd>
        <dt className={styles.term}>배송지</dt>
        <dd className="min-w-0">
          <QuoteDestinationsTable destinations={detail.destinations} />
        </dd>
        <dt className={styles.term}>회사·단체명</dt>
        <dd>{detail.companyName ?? <span className={styles.empty}>-</span>}</dd>
        <dt className={styles.term}>담당자명</dt>
        <dd>{detail.contactName}</dd>
        <dt className={styles.term}>요청 메모</dt>
        <dd className="whitespace-pre-wrap">
          {detail.memo ?? <span className={styles.empty}>-</span>}
        </dd>
        <dt className={styles.term}>디자인</dt>
        <dd>{detail.design.designName ?? '없음'}</dd>
      </dl>
    </ContentCard>
  );
};
